import { Selection, EnterElement, ScaleTime } from "d3"
import { D3OneToOneRenderable } from "./D3OneToOneRenderable"
import { ReactCallbacks } from "../../Types/ReactCallbacks"
import { TimeSeriesPageManager } from "../../Data/TimeSeriesPageManager"
import { ModalityPage } from "../../Data/ModalityPage"
import { getRealtimeValue } from "../../Data/TimeSeriesData"
import { MobergFontFamily } from "../../../../../../Moberg"
import { DeltaRegionJSON, LineTraceConfigJSON, RenderStrategy } from "../../Types/Trace"
import { SummaryValueConfig } from "../../Types/Graph"

export type D3SummaryValuesConfig = {
    traces?: (LineTraceConfigJSON | DeltaRegionJSON)[]
    values?: SummaryValueConfig[]
    rows: number
    columns: number
    height: number
    width: number
    x: number
    y: number
}

type CustomCanvasRenderingContext2d = CanvasRenderingContext2D & {
    textRendering: "optimizeSpeed" | "optimizeLegibility" | "optimizePrecision"
}

export class D3SummaryValues extends D3OneToOneRenderable<SVGGElement, SVGForeignObjectElement, D3SummaryValuesConfig> {
    private pageManager: TimeSeriesPageManager<ModalityPage>
    private viewScale: ScaleTime<any, any, any>
    private context: CustomCanvasRenderingContext2d | null = null
    private currentValues: Map<string, number | undefined> = new Map() // The current value of each trace, by ID
    private visibleTraces: (LineTraceConfigJSON | DeltaRegionJSON)[] = []
    private visibleValues: SummaryValueConfig[] = []
    private colorSchemeObserver: MutationObserver
    private textSizeCache: Map<string, number> = new Map() // string value to font size to prevent expensive re-measureing to find the text size.
    private isDarkMode: boolean = document.documentElement.getAttribute("data-color-scheme") === "dark"

    constructor(root: SVGGElement, config: D3SummaryValuesConfig, className: string, reactCallbacks: ReactCallbacks<any>, pageManager: TimeSeriesPageManager<ModalityPage>, viewScale: ScaleTime<any, any, any>) {
        super(root, config, className, reactCallbacks)
        this.pageManager = pageManager
        this.viewScale = viewScale

        this.colorSchemeObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                if (mutation.attributeName === "data-color-scheme") {
                    this.isDarkMode = document.documentElement.getAttribute("data-color-scheme") === "dark"
                    this.render()
                    break
                }
            }
        })
        this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })

        this.updateDerivedState()
        this.render()
    }

    public destroy() {
        this.colorSchemeObserver.disconnect()
    }

    protected enter(newElements: Selection<EnterElement, D3SummaryValuesConfig, any, any>): Selection<SVGForeignObjectElement, D3SummaryValuesConfig, any, any> {
        const foreignObject = newElements.append("foreignObject")
            .attr("class", this.className)
            .attr("width", this.config.width)
            .attr("height", this.config.height)
            .attr("transform", `translate(${this.config.x}, ${this.config.y})`)

        const canvas = foreignObject.append("xhtml:canvas") as Selection<HTMLCanvasElement, D3SummaryValuesConfig, any, any>

        canvas
            .attr("width", this.config.width)
            .attr("height", this.config.height)

        const canvasNode = canvas.node()

        if (canvasNode != null) {
            this.context = canvasNode.getContext("2d") as CustomCanvasRenderingContext2d ?? null
            this.context.textRendering = "optimizeSpeed"
        }

        this.drawCanvas()

        return foreignObject
    }

    protected update(updatedElements: Selection<SVGForeignObjectElement, D3SummaryValuesConfig, any, any>): Selection<SVGForeignObjectElement, D3SummaryValuesConfig, SVGGElement, any> {
        updatedElements
            .attr("width", this.config.width)
            .attr("height", this.config.height)
            .attr("transform", `translate(${this.config.x}, ${this.config.y})`)

        updatedElements
            .select("canvas")
            .attr("width", this.config.width)
            .attr("height", this.config.height)

        this.setCurrentValue()
        this.drawCanvas()

        return updatedElements
    }

    protected updateDerivedState(): void {
        this.visibleTraces = this.config.traces?.filter(trace => trace.summaryValueConfig && trace.summaryValueConfig.isVisible) ?? []
        this.visibleValues = this.config.values?.filter(config => config.isVisible) ?? []
        this.textSizeCache.clear()
    }

    protected setCurrentValue() {
        this.visibleTraces.forEach(trace => {
            if (trace.renderStrategy === RenderStrategy.DELTA) {
                const firstDataObjectId = this.reactCallbacks.dataSourceMap.get(trace.first.dataSource) ?? Infinity
                const secondDataObjectId = this.reactCallbacks.dataSourceMap.get(trace.second.dataSource) ?? Infinity
                const firstValue = getRealtimeValue(firstDataObjectId, trace.first.dataKey, this.pageManager, trace.first, this.viewScale)
                const secondValue = getRealtimeValue(secondDataObjectId, trace.second.dataKey, this.pageManager, trace.second, this.viewScale)
                this.currentValues.set(trace.id, firstValue !== undefined && secondValue !== undefined ? firstValue - secondValue : undefined)
            } else {
                const dataObjectId = this.reactCallbacks.dataSourceMap.get(trace.dataSource) ?? Infinity
                this.currentValues.set(trace.id, getRealtimeValue(dataObjectId, trace.dataKey, this.pageManager, trace, this.viewScale))
            }
        })

        this.visibleValues.forEach(config => {
            this.currentValues.set(config.name, config.value)
        })
    }

    private drawCanvas() {
        if (!this.context) {
            return
        }

        const cols = this.config.columns ?? 1
        const rows = this.config.rows ?? 1

        const cellWidth = this.config.width / cols
        const cellHeight = Math.min(this.config.width * (this.config.traces?.length ?? this.config.values?.length ?? 1) / cols, this.config.height) / rows

        this.context.clearRect(0, 0, this.config.width, this.config.height)

        this.visibleTraces.forEach((trace, i) => {
            const col = i % cols
            const row = Math.floor(i / cols)
            const x = col * cellWidth
            const y = row * cellHeight

            let color: string

            if (trace.renderStrategy === RenderStrategy.DELTA) {
                color = `rgb(${trace.color.r}, ${trace.color.g}, ${trace.color.b})`
            } else {
                color = trace.color
            }

            const value = this.currentValues.get(trace.id)
            const text = (value !== undefined && !Number.isNaN(value)) ? `${value.toFixed(trace.summaryValueConfig?.precision ?? 0)}${trace.summaryValueConfig?.isPercent ? "%" : ""}` : "?"
            this.drawCell(x, y, cellWidth, cellHeight, text, trace.name, color)
        })

        this.visibleValues.forEach((config, i) => {
            let index = this.visibleTraces.length + i
            const col = index % cols
            const row = Math.floor(index / cols)
            const x = col * cellWidth
            const y = row * cellHeight

            const value = this.currentValues.get(config.name)
            const text = (value !== undefined && !Number.isNaN(value)) ? `${value.toFixed(config.precision ?? 0)}${config?.isPercent ? "%" : ""}` : "?"
            this.drawCell(x, y, cellWidth, cellHeight, text, config.name, config.color)
        })
    }

    private getTextColor(traceColor: string): string {
        return this.isDarkMode ? "white" : traceColor
    }

    private drawCell(x: number, y: number, width: number, height: number, valueText: string, labelText: string, color: string) {
        if (!this.context) {
            return
        }

        const textColor = this.getTextColor(color)

        // Dynamic font sizing
        const horizontalPadding = 10
        const verticalPadding = 2

        const valueFont = this.fitText(valueText, width - 2 * horizontalPadding, height * 0.6)
        this.context.font = valueFont
        this.context.fillStyle = textColor
        this.context.textAlign = "left"
        this.context.textBaseline = "top"
        this.context.fillText(valueText, x + horizontalPadding, y + 2 * verticalPadding + height * 0.3)

        // Label text (smaller)
        const labelFont = this.fitText(labelText, width - 2 * horizontalPadding, height * 0.3)
        this.context.font = labelFont
        this.context.fillStyle = textColor
        this.context.textBaseline = "top"
        this.context.fillText(labelText, x + horizontalPadding, y + verticalPadding)
    }

    private fitText(text: string, maxWidth: number, maxHeight: number) {
        if (!this.context) {
            return `40px ${MobergFontFamily.REGULAR}`
        }

        // Since numbers all have the same character width, we can make cache lookups more efficient by making them generic
        // e.g. "7000" -> "DDDD", "-0.1" -> "-D.D" instead of storing every unique value.
        const textNormalized = text.replace(/\d/g, 'D') 
        const cacheKey = `${textNormalized}|${maxWidth}|${maxHeight}`
        const cachedSize = this.textSizeCache.get(cacheKey)

        if (cachedSize) {
            return `${cachedSize}px ${MobergFontFamily.REGULAR}`
        }

        let min = 6, max = 100
        let best = min

        while (min <= max) {
            const mid = Math.floor((min + max) / 2)
            this.context.font = `${mid}px ${MobergFontFamily.REGULAR}`
            const { width } = this.context.measureText(text)
            const actualHeight = mid

            if (width <= maxWidth && actualHeight <= maxHeight) {
                best = mid
                min = mid + 1
            } else {
                max = mid - 1
            }
        }

        // Prevent the cache from growing too large.
        if (this.textSizeCache.size >= 1000) {
            this.textSizeCache.clear()
        }

        this.textSizeCache.set(cacheKey, best)

        return `${best}px ${MobergFontFamily.REGULAR}`
    }
}
